import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { volunteer as volunteerApi } from "@/lib/api";
import {
  Users,
  LogOut,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  MessageSquare,
  User,
  ChevronDown,
  ChevronRight,
  Trash2,
  AlertTriangle,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  contacted: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  submitted: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const STATUS_OPTIONS = ["pending", "contacted", "in_progress", "submitted", "cancelled"];

export default function VolunteerDashboard() {
  const { logout, user, role } = useAuth();
  const [consents, setConsents] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  const fetchConsents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await volunteerApi.listConsents();
      setConsents(res.data.consents || []);
      setByStatus(res.data.by_status || {});
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  const handleClaim = async (recordId) => {
    try {
      await volunteerApi.claimConsent(recordId);
      fetchConsents();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to claim");
    }
  };

  const handleStartEdit = (consent) => {
    setEditingId(consent.id);
    setEditNotes(consent.notes || "");
    setEditStatus(consent.status || "pending");
  };

  const handleSaveEdit = async (recordId) => {
    setSaving(true);
    try {
      await volunteerApi.updateConsent(recordId, {
        status: editStatus,
        notes: editNotes,
      });
      setEditingId(null);
      fetchConsents();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleRecommendDeletion = async (recordId) => {
    if (!deleteReason.trim()) {
      alert("Please provide a reason for deletion");
      return;
    }
    try {
      await volunteerApi.recommendDeletion(recordId, deleteReason);
      setDeletingId(null);
      setDeleteReason("");
      fetchConsents();
      alert("Deletion recommended. Admin will review.");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to recommend deletion");
    }
  };

  const handleDelete = async (recordId) => {
    if (!confirm("Are you sure you want to permanently delete this record? This cannot be undone.")) {
      return;
    }
    try {
      await volunteerApi.deleteConsent(recordId);
      fetchConsents();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    }
  };

  const filteredConsents = filter === "all" 
    ? consents 
    : consents.filter(c => c.status === filter);

  const formatDate = (isoStr) => {
    if (!isoStr) return "-";
    return new Date(isoStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/aht_round_orange.png" alt="AHT" className="h-8 w-8 rounded-full" />
            <div>
              <h1 className="text-lg font-semibold">Volunteer Dashboard</h1>
              <p className="text-xs text-slate-400">Manage consent requests</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400">
              <User size={14} className="inline mr-1" />
              {user} ({role})
            </span>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded transition"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {STATUS_OPTIONS.map((status) => (
            <div
              key={status}
              onClick={() => setFilter(filter === status ? "all" : status)}
              className={`p-4 rounded-lg cursor-pointer transition ${
                filter === status ? "ring-2 ring-orange-500" : ""
              } ${STATUS_COLORS[status].replace("text-", "bg-opacity-20 text-")}`}
            >
              <div className="text-2xl font-bold">{byStatus[status] || 0}</div>
              <div className="text-sm capitalize">{status.replace("_", " ")}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">
            {filter === "all" ? "All Requests" : `${filter.replace("_", " ")} Requests`}
            <span className="text-slate-400 ml-2">({filteredConsents.length})</span>
          </h2>
          <button
            onClick={fetchConsents}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Consent List */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : filteredConsents.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            No requests found. {filter !== "all" && (
              <button onClick={() => setFilter("all")} className="text-orange-400 hover:underline ml-1">
                Show all
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConsents.map((consent) => (
              <div
                key={consent.id}
                className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden"
              >
                {/* Header Row */}
                <div
                  className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-750"
                  onClick={() => setExpandedId(expandedId === consent.id ? null : consent.id)}
                >
                  {expandedId === consent.id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{consent.name || "No name"}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[consent.status]}`}>
                        {consent.status?.replace("_", " ")}
                      </span>
                      {consent.deletion_recommended && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-900 text-red-300 flex items-center gap-1">
                          🔒 Locked for deletion
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                      {consent.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={12} /> {consent.email}
                        </span>
                      )}
                      {consent.mobile && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} /> {consent.mobile}
                        </span>
                      )}
                      {consent.postcode && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {consent.postcode}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right text-sm text-slate-400">
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatDate(consent.created_at)}
                    </div>
                    {consent.assigned_to && (
                      <div className="text-xs text-orange-400 mt-1">
                        Assigned: {consent.assigned_to}
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedId === consent.id && (
                  <div className="border-t border-slate-700 p-4 bg-slate-850">
                    {/* Current Notes */}
                    {consent.notes && (
                      <div className="mb-4 p-3 bg-slate-900 rounded text-sm">
                        <div className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                          <MessageSquare size={12} /> Notes
                        </div>
                        {consent.notes}
                      </div>
                    )}

                    {/* Action Log */}
                    {consent.action_log && consent.action_log.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs text-slate-400 mb-2">History</div>
                        <div className="space-y-1 text-xs">
                          {consent.action_log.map((log, i) => (
                            <div key={i} className="flex items-center gap-2 text-slate-500">
                              <span>{formatDate(log.timestamp)}</span>
                              <span className="text-slate-400">—</span>
                              <span>{log.by}</span>
                              {log.status_change && <span className="text-blue-400">{log.status_change}</span>}
                              {log.action && <span className="text-green-400">{log.action}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Edit Form */}
                    {editingId === consent.id ? (
                      <div className="space-y-3 p-3 bg-slate-900 rounded">
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">Status</label>
                          <select
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm"
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>{s.replace("_", " ")}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">Notes</label>
                          <textarea
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            placeholder="Add notes about actions taken..."
                            rows={3}
                            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm resize-none"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveEdit(consent.id)}
                            disabled={saving}
                            className="flex-1 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 px-4 py-2 rounded text-sm font-medium transition"
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : deletingId === consent.id ? (
                      /* Deletion reason form */
                      <div className="space-y-3 p-3 bg-red-900/20 border border-red-800 rounded">
                        <div className="flex items-center gap-2 text-red-400 text-sm">
                          <AlertTriangle size={16} />
                          {role === "admin" ? "Confirm Deletion" : "Recommend Deletion"}
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">Reason for deletion</label>
                          <textarea
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            placeholder="e.g., Duplicate entry, Invalid contact details, Test submission..."
                            rows={2}
                            className="w-full bg-slate-800 border border-red-700 rounded px-3 py-2 text-sm resize-none"
                          />
                        </div>
                        <div className="flex gap-2">
                          {role === "admin" ? (
                            <button
                              onClick={() => handleDelete(consent.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 rounded text-sm font-medium transition"
                            >
                              <Trash2 size={14} /> Delete Permanently
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRecommendDeletion(consent.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 rounded text-sm font-medium transition"
                            >
                              <AlertTriangle size={14} /> Send to Admin
                            </button>
                          )}
                          <button
                            onClick={() => { setDeletingId(null); setDeleteReason(""); }}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : consent.deletion_recommended ? (
                      /* Record is locked for deletion */
                      <div className="p-3 bg-red-900/20 border border-red-800 rounded">
                        <div className="flex items-center gap-2 text-red-400 mb-2">
                          <AlertTriangle size={16} />
                          <span className="font-medium">🔒 Record Locked for Deletion Review</span>
                        </div>
                        <div className="text-sm text-slate-300 mb-2">
                          Requested by <strong>{consent.deletion_recommended_by}</strong> on {formatDate(consent.deletion_recommended_at)}
                        </div>
                        <div className="text-sm text-slate-400 mb-3">
                          Reason: {consent.deletion_reason || "No reason provided"}
                        </div>
                        <div className="text-xs text-slate-500 mb-3">
                          This record cannot be edited. Admin must approve deletion or cancel the request.
                        </div>
                        {role === "admin" && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDelete(consent.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 rounded text-sm font-medium transition"
                            >
                              <Trash2 size={14} /> Confirm Delete
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await volunteerApi.cancelDeletion(consent.id);
                                  fetchConsents();
                                } catch (err) {
                                  alert(err.response?.data?.detail || "Failed to cancel deletion");
                                }
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm transition"
                            >
                              <XCircle size={14} /> Cancel Deletion Request
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {!consent.assigned_to && (
                          <button
                            onClick={() => handleClaim(consent.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm font-medium transition"
                          >
                            <User size={14} /> Claim This
                          </button>
                        )}
                        <button
                          onClick={() => handleStartEdit(consent)}
                          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm transition"
                        >
                          <MessageSquare size={14} /> Update Status / Notes
                        </button>
                        {consent.status !== "submitted" && (
                          <button
                            onClick={async () => {
                              await volunteerApi.updateConsent(consent.id, { status: "submitted" });
                              fetchConsents();
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 rounded text-sm font-medium transition"
                          >
                            <CheckCircle2 size={14} /> Mark Submitted
                          </button>
                        )}
                        {/* Request deletion button */}
                        <button
                          onClick={() => setDeletingId(consent.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded text-sm transition ${
                            role === "admin" 
                              ? "bg-red-600 hover:bg-red-500" 
                              : "bg-slate-700 hover:bg-slate-600 text-red-400"
                          }`}
                        >
                          <Trash2 size={14} /> {role === "admin" ? "Delete" : "Request Deletion"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
